import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { validateDeck, type Annotation, type Deck } from "@deckloom/core/schema";
import { compileSteps } from "@deckloom/core/timeline";
import { DEFAULT_TOOL, Stage, type InkTool, type ToolKind } from "@deckloom/player";
import { Toolbar } from "./Toolbar";

/**
 * Local preview of a converted deck folder (see packages/pptx/scripts/convert.ts), no Worker needed.
 *   /preview?deck=/preview/name/deck.json&page=3&step=last
 * Arrow keys change the page; `step` defaults to the final animation state so every element is visible.
 */
export function PreviewPage() {
  const [search, setSearch] = useSearchParams();
  const deckUrl = search.get("deck") ?? "/preview/deck.json";
  const pageIndex = Math.max(0, Number(search.get("page") ?? "1") - 1);
  const stepParam = search.get("step") ?? "last";
  const [deck, setDeck] = useState<Deck | null>(null);
  const [error, setError] = useState<string | null>(null);
  // local-only annotation tools (no room): handy for trying the toolbar
  const [strokes, setStrokes] = useState<Record<string, Annotation[]>>({});
  const [toolOpts, setToolOpts] = useState<InkTool>(DEFAULT_TOOL);
  const [toolOn, setToolOn] = useState(false);
  const tool = toolOn ? toolOpts : null;
  const pickTool = useCallback((kind: ToolKind) => {
    setToolOpts((t) => ({ ...t, kind }));
    setToolOn((on) => !(on && toolOpts.kind === kind));
  }, [toolOpts.kind]);

  useEffect(() => {
    let alive = true;
    fetch(deckUrl)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status} ${deckUrl}`))))
      .then((j) => alive && setDeck(validateDeck(j)))
      .catch((e) => alive && setError(String(e)));
    return () => { alive = false; };
  }, [deckUrl]);

  const base = deckUrl.replace(/[^/]*$/, "");
  const mediaUrl = useMemo(() => (id: string) => `${base}${deck?.media[id]?.key ?? `media/${id}`}`, [base, deck]);

  useEffect(() => {
    if (!deck) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "TEXTAREA") return;
      if (e.key === "Escape") { setToolOn(false); return; }
      const delta = e.key === "ArrowRight" || e.key === " " ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (!delta) return;
      const next = Math.min(deck.pages.length, Math.max(1, pageIndex + 1 + delta));
      setSearch((s) => { s.set("page", String(next)); return s; });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deck, pageIndex, setSearch]);

  if (error) return <main className="center"><p className="error">{error}</p></main>;
  if (!deck) return <main className="center"><p>Loading…</p></main>;
  const page = deck.pages[Math.min(pageIndex, deck.pages.length - 1)]!;
  const steps = compileSteps(page.timeline);
  const step = stepParam === "last" ? steps.length - 1 : Math.min(steps.length - 1, Number(stepParam));
  return (
    <div className="preview" style={{ ["--slide-aspect" as string]: `${deck.size.w} / ${deck.size.h}` }}>
      <div className="stage-wrap">
        <Stage
          deck={deck}
          pageIndex={page.index}
          step={step}
          mediaUrl={mediaUrl}
          playUrl={mediaUrl}
          presenter
          strokes={strokes[page.id] ?? []}
          inkTool={tool}
          onStroke={(a) => setStrokes((s) => ({ ...s, [page.id]: [...(s[page.id] ?? []), a] }))}
          onStrokeRemove={(id) => setStrokes((s) => ({ ...s, [page.id]: (s[page.id] ?? []).filter((x) => x.id !== id) }))}
          onNavigate={(t) => {
            const next = t.kind === "page" ? deck.pages.findIndex((p) => p.id === t.pageId) + 1 : t.kind === "next" ? pageIndex + 2 : t.kind === "prev" ? pageIndex : t.kind === "first" ? 1 : deck.pages.length;
            if (next >= 1 && next <= deck.pages.length) setSearch((s) => { s.set("page", String(next)); return s; });
          }}
        />
      </div>
      <div className="preview-info bar">
        <span>{page.index + 1} / {deck.pages.length}{page.hidden ? " (hidden)" : ""} · {page.elements.length} elements · {steps.length} steps</span>
        <Toolbar
          tool={tool}
          opts={toolOpts}
          onPick={pickTool}
          onChange={(patch) => { setToolOpts((t) => ({ ...t, ...patch })); setToolOn(true); }}
          onShape={(shape) => { setToolOpts((t) => ({ ...t, kind: "shape", shape })); setToolOn(true); }}
          onUndo={() => setStrokes((s) => ({ ...s, [page.id]: (s[page.id] ?? []).slice(0, -1) }))}
          onClear={() => setStrokes((s) => ({ ...s, [page.id]: [] }))}
        />
      </div>
    </div>
  );
}
